/**
 * ObjectiveChips — Impact / Effort / "moves" pills for one recommendation
 * objective. Shared by RecommendationCard (Recommendations tab) and the
 * Action Plan task list so the indicators stay identical.
 *
 * Impact: more is better (High = green). Effort: less is better (Low = green).
 * The `moves` pill is neutral and carries the element's current score in its
 * own ring colour.
 */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { formatBipolarScore } from '../config/bandColours';
import { colourForScore } from '../config/elementColours';
import type { ScoreNode } from '../types';

export interface Objective {
  task?: string;
  detail?: string;
  impact?: string;
  effort?: string;
  moves?: string;
}

type Palette = { color: string; border: string; bg: string; dot: string };
const GOOD: Palette = { color: '#047857', border: '#A7F3D0', bg: '#ECFDF5', dot: '#10B981' };
const WARN: Palette = { color: '#B45309', border: '#FCD974', bg: '#FEF9EC', dot: '#F59E0B' };
const BAD: Palette = { color: '#B91C1C', border: '#FECACA', bg: '#FEF2F2', dot: '#EF4444' };
const NEUTRAL: Palette = { color: '#64748B', border: '#E2E8F0', bg: '#F1F5F9', dot: '#94A3B8' };

// The chip holds one word. The Briefing writes "High" / "Med" / "Low"; the
// Model Beliefs synthesis has written whole sentences ("Low: a single page
// that can be written once"), and a sentence in a pill is a blob. Take the
// level word when the value starts with one; otherwise there is no chip.
export const objectiveLevel = (v?: string): string | undefined => {
  const match = (v ?? '').match(/^\s*(high|medium|med|low)\b/i);
  if (!match) return undefined;
  const word = match[1].toLowerCase();
  return word === 'high' ? 'High' : word === 'low' ? 'Low' : 'Med';
};

const impactPalette = (v?: string): Palette => {
  const x = (v ?? '').toLowerCase();
  return x.startsWith('high') ? GOOD : x.startsWith('med') ? WARN : NEUTRAL;
};
const effortPalette = (v?: string): Palette => {
  const x = (v ?? '').toLowerCase();
  return x.startsWith('low') ? GOOD : x.startsWith('med') ? WARN : BAD;
};

const Chip: React.FunctionComponent<{
  palette: Palette;
  showDot?: boolean;
  children: React.ReactNode;
}> = ({ palette, showDot = true, children }) => (
  <span
    style={{
      fontSize: 12,
      fontWeight: 600,
      borderRadius: 999,
      padding: '4px 11px',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      border: `1px solid ${palette.border}`,
      background: palette.bg,
      color: palette.color,
    }}
  >
    {showDot && (
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: palette.dot }} />
    )}
    {children}
  </span>
);

export const ObjectiveChips: React.FunctionComponent<{
  objective: Objective;
  resolve: (name: string) => ScoreNode | undefined;
}> = ({ objective: o, resolve }) => {
  const node = o.moves ? resolve(o.moves) : undefined;
  const impact = objectiveLevel(o.impact);
  const effort = objectiveLevel(o.effort);
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
      {impact && (
        <Chip palette={impactPalette(impact)}>
          <FormattedMessage
            id="marketerView.recos.impact"
            defaultMessage="Impact {v}"
            values={{ v: impact }}
          />
        </Chip>
      )}
      {effort && (
        <Chip palette={effortPalette(effort)}>
          <FormattedMessage
            id="marketerView.recos.effort"
            defaultMessage="Effort {v}"
            values={{ v: effort }}
          />
        </Chip>
      )}
      {o.moves && (
        <Chip palette={NEUTRAL} showDot={false}>
          <span style={{ color: '#94A3B8', fontWeight: 600 }}>moves&nbsp;</span>
          {node ? node.display : o.moves}
          {node && node.value != null && (
            <span
              style={{
                marginLeft: 6,
                fontWeight: 800,
                color: colourForScore(node.canonical, node.value),
              }}
            >
              {formatBipolarScore(node.value)}
            </span>
          )}
        </Chip>
      )}
    </div>
  );
};
